import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import ApiClient from '../utils/apiClient';
import type { ApiError } from '../utils/apiClient';
import { DPA_PATH, DPA_VERSION, DPA_VERSION_LABEL } from '../utils/dpa';
import styles from './ReleaseScheduleDialog.module.css';

// Seiten, die ohne Zustimmung erreichbar bleiben: der Vertrag selbst, Rechtliches, Hilfe,
// Kontakt – und das Profil, damit man das Konto auch ohne Zustimmung löschen kann.
const EXEMPT_PREFIXES = [DPA_PATH, '/datenschutz', '/impressum', '/hilfe', '/contact', '/profile'];

/**
 * Zustimmung zum AV-Vertrag (Art. 28 DSGVO) für Lehrkräfte, die ihm noch nicht oder nur in
 * einer älteren Fassung zugestimmt haben. Neue Lehrkräfte stimmen schon bei der Registrierung
 * zu; das hier erfasst Bestandskonten und von Admins angelegte Konten.
 *
 * Blockierend: Ohne Zustimmung darf wankandoo die Daten der Studierenden nicht weiter im
 * Auftrag der Lehrkraft verarbeiten. Bei Impersonation erscheint der Dialog nicht – ein
 * Admin darf nicht im Namen der Lehrkraft zustimmen (der Server lehnt das auch ab).
 */
export default function DpaConsentGate() {
  const { isLoggedIn, userRole, user, isImpersonating, refreshUser, logout } = useAuth();
  const { pathname } = useLocation();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [acceptedNow, setAcceptedNow] = useState(false);

  const needsConsent =
    isLoggedIn &&
    userRole === 'teacher' &&
    !isImpersonating &&
    // Erst wenn der Server den Stand meldet (Backend mit AV-Vertrag deployt).
    Boolean(user?.dpa_current_version) &&
    user?.dpa_version !== DPA_VERSION &&
    !acceptedNow;

  if (!needsConsent || EXEMPT_PREFIXES.some(p => pathname.startsWith(p))) return null;

  const isUpdate = Boolean(user?.dpa_version);

  const accept = async () => {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      await ApiClient.post('me/dpa/accept', { version: DPA_VERSION });
      setAcceptedNow(true);
      await refreshUser();
    } catch (caught) {
      const err = caught as ApiError;
      setError(err.originalErrorData?.detail || err.message || 'Das hat nicht geklappt. Bitte versuche es noch einmal.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className={styles.overlay} role="presentation">
      <div className={styles.dialog} role="dialog" aria-modal="true" aria-labelledby="dpa-dialog-title" data-testid="dpa-consent-dialog">
        <h2 id="dpa-dialog-title" className={styles.title}>
          {isUpdate ? 'Der AV-Vertrag wurde aktualisiert' : 'Vertrag zur Auftragsverarbeitung'}
        </h2>
        <div className={styles.body}>
          <p>
            Du speicherst in wankandoo Daten deiner Studierenden – Namen, E-Mail-Adressen und Zeiten.
            Wir verarbeiten sie in deinem Auftrag. Die Datenschutz-Grundverordnung verlangt dafür einen
            Vertrag zur Auftragsverarbeitung (Art.&nbsp;28 DSGVO) zwischen dir und uns.
          </p>
          <p>
            <a href={DPA_PATH} target="_blank" rel="noopener noreferrer">Vertrag lesen (Fassung vom {DPA_VERSION_LABEL})</a>
            {' '}– du kannst ihn dort auch drucken oder als PDF speichern.
          </p>
          <p className={styles.muted}>
            Ohne Zustimmung kannst du wankandoo als Lehrkraft nicht weiter nutzen. Dein Konto kannst du
            jederzeit unter <Link to="/profile">Profil</Link> löschen.
          </p>
          {error && <p role="alert" style={{ color: 'var(--rose-dark, #9e526e)' }}>{error}</p>}
        </div>
        <div className={styles.actions}>
          <button type="button" className={styles.cancel} onClick={() => { void logout(); }} disabled={busy}>
            Abmelden
          </button>
          <button type="button" className={styles.confirm} onClick={accept} disabled={busy} data-testid="dpa-accept">
            {busy ? 'Speichere …' : 'Ich stimme dem Vertrag zu'}
          </button>
        </div>
      </div>
    </div>
  );
}
